import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const exploreIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" />
  </svg>
);

const authedTabs = [
  {
    to: '/rooms',
    label: 'Rooms',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    to: '/explore',
    label: 'Explore',
    icon: exploreIcon,
  },
  {
    to: '/studio',
    label: 'Studio',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        <path d="M4.93 4.93a10 10 0 0 0 0 14.14" />
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Hide bottom tabs in Chat and Onboarding for more vertical space
  const hideTabBar =
    location.pathname.startsWith('/rooms/') ||
    location.pathname === '/onboarding';

  return (
    <div className="flex flex-col h-dvh max-w-lg mx-auto pt-[env(safe-area-inset-top)]">
      <main className="flex-1 overflow-y-auto">{children}</main>

      {/* Bottom tabs — hidden in chat & onboarding */}
      {!hideTabBar && (
        <nav
          className="flex border-t border-sage-800 bg-dark-100 pb-[env(safe-area-inset-bottom)]"
          data-testid="bottom-nav"
        >
          {user ? (
            // Authenticated: full nav
            authedTabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === '/rooms'}
                data-testid={`nav-${tab.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center py-3 text-xs transition-colors ${
                    isActive ? 'text-gold' : 'text-gray-500 hover:text-gray-300'
                  }`
                }
              >
                <span className="mb-0.5">{tab.icon}</span>
                {tab.label}
              </NavLink>
            ))
          ) : (
            // Guest: just explore + sign in
            <>
              <NavLink
                to="/explore"
                data-testid="nav-explore"
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center py-3 text-xs transition-colors ${
                    isActive ? 'text-gold' : 'text-gray-500 hover:text-gray-300'
                  }`
                }
              >
                <span className="mb-0.5">{exploreIcon}</span>
                Explore
              </NavLink>
              <button
                onClick={() => navigate('/auth')}
                data-testid="nav-signin"
                className="flex-1 flex flex-col items-center py-3 text-xs transition-colors text-gray-500 hover:text-gold"
              >
                <span className="mb-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                </span>
                Sign In
              </button>
            </>
          )}
        </nav>
      )}
    </div>
  );
}
